/**
 * Histórico (RF07): monta itens read-only do detalhe para os cartões do relatório.
 */

import type { RelatorioAplicacaoApi, SimulacaoDetalheApi } from "../simulacao.gateway";
import { isRecord, readFirstText } from "@/core/utils";
import { converterItensCenario } from "./simulacao.instrucao";
import { itensDaAplicacao, type EstadoAplicacao, type ItemAplicacao } from "./simulacao.aplicacao";

interface ItemRelatorioLido {
  chave: string;
  operacao: string;
  estado: EstadoAplicacao;
  mensagem: string;
}

function normalizarEstadoRelatorio(bruto: string): EstadoAplicacao | null {
  if (bruto === "aplicadaComAviso") return "aplicada-com-aviso";
  if (bruto === "naoAplicada") return "nao-aplicada";
  if (bruto === "aplicada") return "aplicada";
  if (bruto === "pendente") return "pendente";
  if (bruto === "descartada") return "descartada";
  return null;
}

function lerEstadoItem(rawItem: unknown): ItemRelatorioLido | null {
  if (!isRecord(rawItem)) return null;
  const estadoBruto = (readFirstText(rawItem, "estado") || readFirstText(rawItem, "status")).trim();
  const estado = normalizarEstadoRelatorio(estadoBruto);
  if (!estado) return null;
  return {
    chave: readFirstText(rawItem, "chave").trim(),
    operacao: readFirstText(rawItem, "operacao").trim(),
    estado,
    mensagem: readFirstText(rawItem, "mensagem").trim(),
  };
}

function aplicarRelatorio(
  itensBase: ItemAplicacao[],
  relatorio: RelatorioAplicacaoApi,
): ItemAplicacao[] {
  const ESTADOS_FINAIS = new Set<EstadoAplicacao>([
    "aplicada",
    "aplicada-com-aviso",
    "nao-aplicada",
    "descartada",
  ]);

  const itensRelatorio = (Array.isArray(relatorio.itens) ? relatorio.itens : [])
    .map(lerEstadoItem)
    .filter((item): item is ItemRelatorioLido => item !== null);

  const usados = new Set<number>();

  function dadosDoItem(item: ItemAplicacao, index: number): ItemRelatorioLido | undefined {
    const porChave = itensRelatorio.findIndex(
      (relatorioItem, relatorioIndex) =>
        !usados.has(relatorioIndex) &&
        relatorioItem.chave !== "" &&
        relatorioItem.chave === item.chave,
    );
    if (porChave >= 0) {
      usados.add(porChave);
      return itensRelatorio[porChave];
    }

    const porOperacao = itensRelatorio
      .map((relatorioItem, relatorioIndex) => ({ relatorioItem, relatorioIndex }))
      .filter(
        ({ relatorioItem, relatorioIndex }) =>
          !usados.has(relatorioIndex) && relatorioItem.operacao === item.alteracao.acao,
      );
    const unico = porOperacao.length === 1 ? porOperacao[0] : undefined;
    if (unico) {
      usados.add(unico.relatorioIndex);
      return unico.relatorioItem;
    }

    if (itensRelatorio.length === itensBase.length && !usados.has(index)) {
      usados.add(index);
      return itensRelatorio[index];
    }

    return undefined;
  }

  return itensBase.map((item, index) => {
    const dadosRelatorio = dadosDoItem(item, index);
    if (dadosRelatorio?.estado === "pendente") {
      return { ...item, estado: "pendente", mensagem: null, motivo: null };
    }
    if (dadosRelatorio && ESTADOS_FINAIS.has(dadosRelatorio.estado)) {
      return {
        ...item,
        estado: dadosRelatorio.estado,
        mensagem: dadosRelatorio.mensagem || "Aplicado.",
        motivo: dadosRelatorio.estado === "nao-aplicada" ? "negocio" : null,
      };
    }
    if (relatorio.status === "APLICADA" || relatorio.status === "APLICADA_PARCIAL") {
      return { ...item, estado: "aplicada", mensagem: "Aplicado.", motivo: null };
    }
    return {
      ...item,
      estado: "nao-aplicada",
      mensagem: "Não foi possível aplicar.",
      motivo: "negocio",
    };
  });
}

/** Cartões read-only a partir do GET /simulacoes/{id}. */
export function itensDoDetalheHistorico(detalhe: SimulacaoDetalheApi): ItemAplicacao[] {
  const cenario = converterItensCenario(detalhe.itens);
  const base = itensDaAplicacao(cenario);
  if (detalhe.relatorio) {
    return aplicarRelatorio(base, detalhe.relatorio);
  }
  return base;
}
